'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE,
  parseConsentCookie,
  requiresOptIn,
  serializeConsent,
  type ConsentCategories,
  type ConsentRecord,
} from '@/lib/consent';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

interface ConsentContextValue {
  /** Effective consent after geo defaults + stored choice + GPC. */
  consent: ConsentCategories;
  /** Whether the visitor has made an explicit choice this policy version. */
  hasStoredChoice: boolean;
  /** Opt-in jurisdiction (EEA/UK/CH or unknown geo). */
  optInRequired: boolean;
  /** True until cookies are read on the client — render no trackers before. */
  isReady: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  saveConsent: (categories: ConsentCategories) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readCookie(name: string): string | null {
  return (
    document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`))?.[1] ?? null
  );
}

function pushConsentToGtag(consent: ConsentCategories) {
  window.gtag?.('consent', 'update', {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [record, setRecord] = useState<ConsentRecord | null>(null);
  const [optInRequired, setOptInRequired] = useState(true);
  const [gpc, setGpc] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    // Deferred a tick so the cookie read happens strictly after hydration —
    // server HTML always matches the not-ready client render.
    const id = setTimeout(() => {
      setRecord(parseConsentCookie(document.cookie));
      setOptInRequired(requiresOptIn(readCookie('VISITOR_COUNTRY')));
      setGpc(navigator.globalPrivacyControl === true);
      setIsReady(true);
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const consent = useMemo<ConsentCategories>(() => {
    // Explicit choice wins; without one, opt-in regions default to denied and
    // opt-out regions to granted. GPC is an opt-out signal we always honor
    // for marketing/"sharing" (CPRA §1798.135(b)).
    const base = record ?? {
      analytics: !optInRequired,
      marketing: !optInRequired,
    };
    return {
      analytics: base.analytics,
      marketing: base.marketing && !gpc,
    };
  }, [record, optInRequired, gpc]);

  const saveConsent = useCallback(
    (categories: ConsentCategories) => {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${CONSENT_COOKIE}=${serializeConsent(categories)}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
      pushConsentToGtag({
        ...categories,
        marketing: categories.marketing && !gpc,
      });

      // Revoking a category that was active can't unload already-running
      // trackers — reload so the page comes back clean.
      const revoked =
        (consent.analytics && !categories.analytics) ||
        (consent.marketing && !categories.marketing);

      setRecord(parseConsentCookie(document.cookie));
      setIsPreferencesOpen(false);
      if (revoked) window.location.reload();
    },
    [consent, gpc],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      hasStoredChoice: record !== null,
      optInRequired,
      isReady,
      isPreferencesOpen,
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => setIsPreferencesOpen(false),
      saveConsent,
    }),
    [consent, record, optInRequired, isReady, isPreferencesOpen, saveConsent],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}
